import { useEffect, useMemo, useState } from 'react';
import { ProCard, StatisticCard } from '@ant-design/pro-components';
import { App, Col, DatePicker, Row, Segmented, Space, Typography } from 'antd';
import { useRequest } from 'ahooks';
import ReactECharts from 'echarts-for-react';
import dayjs, { type Dayjs } from 'dayjs';
import { FormattedMessage, useIntl } from 'react-intl';

import Permission from '@/components/permission';
import { formatCompact, formatNumber } from '@/utils/number';
import {
    userStatsActiveTrendApi,
    userStatsSummaryApi,
    type ActiveTrendPoint,
    type UserActiveMetric,
    type UserStatsSummary,
} from '@/services/stats';

type RangeValue = [Dayjs | null, Dayjs | null] | null;

const DAY_MS = 86_400_000;

/** UTC 'YYYY-MM-DD' → UTC 零点毫秒（跨日加减不受本地时区/夏令时影响） */
const parseUtcDate = (date: string): number => Date.parse(`${date}T00:00:00.000Z`);

const formatUtcDate = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

const formatUtcMonthDay = (ms: number): string => new Date(ms).toISOString().slice(5, 10);

/** echarts axis tooltip 参数（仅取用到的字段） */
type TooltipParam = { dataIndex: number; marker: string; seriesName: string; value: number | string };

const PageUsersOverview: React.FC = () => {
    /**
     * Hooks
     */
    const { message } = App.useApp();
    const { formatMessage } = useIntl();

    /**
     * State
     */
    const [summary, setSummary] = useState<UserStatsSummary | null>(null);
    const [points, setPoints] = useState<ActiveTrendPoint[]>([]);
    const [metric, setMetric] = useState<UserActiveMetric>('dau');
    const [range, setRange] = useState<RangeValue>(null);

    /**
     * Requests
     */
    const {
        loading: summaryLoading,
        run: runSummary,
    } = useRequest(userStatsSummaryApi, {
        manual: true,
        onSuccess: ({ data: { code, data, msg } }) => {
            if (code !== 200) {
                return message.error(msg);
            }
            setSummary(data);
        },
    });

    const {
        loading: trendLoading,
        run: runTrend,
    } = useRequest(userStatsActiveTrendApi, {
        manual: true,
        onSuccess: ({ data: { code, data, msg } }) => {
            if (code !== 200) {
                return message.error(msg);
            }
            setPoints(data?.points ?? []);
        },
    });

    /**
     * Effects
     */
    useEffect(() => {
        runSummary();
    }, []);

    useEffect(() => {
        const [start, end] = range ?? [];
        runTrend({
            metric,
            start: start ? start.format('YYYY-MM-DD') : undefined,
            end: end ? end.format('YYYY-MM-DD') : undefined,
        });
    }, [metric, range]);

    /**
     * Formatters
     */
    const formatDuration = (totalSeconds: unknown): string => {
        const num = Number(totalSeconds);
        if (!Number.isFinite(num) || num < 0) {
            return '–';
        }
        const h = Math.floor(num / 3600);
        const m = Math.floor((num % 3600) / 60);
        const s = Math.floor(num % 60);
        const parts: string[] = [];
        if (h > 0) {
            parts.push(`${h}${formatMessage({ id: 'p.uo.duration.h' })}`);
        }
        if (m > 0) {
            parts.push(`${m}${formatMessage({ id: 'p.uo.duration.m' })}`);
        }
        if (s > 0 && parts.length < 2) {
            parts.push(`${s}${formatMessage({ id: 'p.uo.duration.s' })}`);
        }
        if (parts.length === 0) {
            return `0${formatMessage({ id: 'p.uo.duration.s' })}`;
        }
        return parts.join(' ');
    };

    const formatPercent = (ratio: number | undefined): string => `${(Number(ratio ?? 0) * 100).toFixed(1)}%`;

    const dataAsOfText = summary?.data_as_of
        ? dayjs(summary.data_as_of).format('YYYY-MM-DD HH:mm:ss [UTC]')
        : '–';

    /**
     * Trend chart
     */
    const metricOptions = [
        { label: formatMessage({ id: 'p.uo.trend.dau' }), value: 'dau' },
        { label: formatMessage({ id: 'p.uo.trend.wau' }), value: 'wau' },
        { label: formatMessage({ id: 'p.uo.trend.mau' }), value: 'mau' },
    ];

    const chartOption = useMemo(() => {
        const now = new Date();
        const todayUtcStart = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
        const [, rangeEnd] = range ?? [];
        // wau 点位 date 为 ISO 周起始（周一）：横坐标与 tooltip 展示周起止；未完周截至今天（自定义区间时截至此处），不显示未来日期
        const capMs = rangeEnd ? Math.min(parseUtcDate(rangeEnd.format('YYYY-MM-DD')), todayUtcStart) : todayUtcStart;

        const weekLabels = points.map(({ date }) => {
            const startMs = parseUtcDate(date);
            const endMs = Math.min(startMs + 6 * DAY_MS, Math.max(capMs, startMs));
            return {
                axis: `${formatUtcMonthDay(startMs)} ~ ${formatUtcMonthDay(endMs)}`,
                full: `${date} ~ ${formatUtcDate(endMs)}`,
            };
        });
        const isWau = metric === 'wau';
        const axisLabels = isWau ? weekLabels.map((label) => label.axis) : points.map((point) => point.date);
        const fullLabels = isWau ? weekLabels.map((label) => label.full) : points.map((point) => point.date);

        return {
            grid: { bottom: 32, left: 56, right: 24, top: 32 },
            tooltip: {
                trigger: 'axis',
                formatter: (params: TooltipParam[]) => {
                    const item = params[0];
                    if (!item) {
                        return '';
                    }
                    return `${item.marker} ${item.seriesName}<br/>${fullLabels[item.dataIndex] ?? ''}<br/>${formatNumber(item.value)}`;
                },
            },
            xAxis: {
                type: 'category',
                boundaryGap: false,
                data: axisLabels,
                axisLabel: { hideOverlap: true },
            },
            yAxis: { minInterval: 1, type: 'value' },
            series: [
                {
                    areaStyle: { opacity: 0.08 },
                    data: points.map((point) => point.value),
                    name: metricOptions.find((option) => option.value === metric)?.label ?? metric,
                    showSymbol: points.length <= 31,
                    smooth: true,
                    type: 'line',
                },
            ],
        };
    }, [points, metric, range]);

    /**
     * KPI cards
     */
    const kpiCards = [
        {
            key: 'totalUsers',
            statistic: {
                title: <FormattedMessage id="p.uo.kpi.totalUsers" />,
                value: formatCompact(summary?.total_users),
                description: (
                    <>
                        <FormattedMessage id="p.uo.kpi.newUsers7d" /> +{formatCompact(summary?.new_users_7d)}
                        {' · '}
                        <FormattedMessage id="p.uo.kpi.newUsers30d" /> +{formatCompact(summary?.new_users_30d)}
                    </>
                ),
            },
        },
        {
            key: 'creatorCount',
            statistic: {
                title: <FormattedMessage id="p.uo.kpi.creatorCount" />,
                value: formatCompact(summary?.creator_count),
                description: (
                    <div>
                        <div>
                            <FormattedMessage id="p.uo.kpi.ratio" /> {formatPercent(summary?.creator_ratio)}
                        </div>
                        <Typography.Text type="secondary">
                            <FormattedMessage id="p.uo.kpi.creatorDesc" />
                        </Typography.Text>
                    </div>
                ),
            },
        },
        {
            key: 'launcherCount',
            statistic: {
                title: <FormattedMessage id="p.uo.kpi.launcherCount" />,
                value: formatCompact(summary?.launcher_count),
                description: (
                    <div>
                        <div>
                            <FormattedMessage id="p.uo.kpi.ratio" /> {formatPercent(summary?.launcher_ratio)}
                        </div>
                        <Typography.Text type="secondary">
                            <FormattedMessage id="p.uo.kpi.launcherDesc" />
                        </Typography.Text>
                    </div>
                ),
            },
        },
        {
            key: 'dau',
            statistic: {
                title: <FormattedMessage id="p.uo.kpi.dau" />,
                value: formatCompact(summary?.dau),
                description: (
                    <FormattedMessage id="p.uo.kpi.dauAvg30d" values={{ value: formatCompact(summary?.dau_30d_avg) }} />
                ),
            },
        },
        {
            key: 'wau',
            statistic: {
                title: <FormattedMessage id="p.uo.kpi.wau" />,
                value: formatCompact(summary?.wau),
                description: <FormattedMessage id="p.uo.kpi.wauDesc" />,
            },
        },
        {
            key: 'mau',
            statistic: {
                title: <FormattedMessage id="p.uo.kpi.mau" />,
                value: formatCompact(summary?.mau),
                description: <FormattedMessage id="p.uo.kpi.mauDesc" />,
            },
        },
        {
            key: 'avgOnline',
            statistic: {
                title: <FormattedMessage id="p.uo.kpi.avgOnline" />,
                value: formatDuration(summary?.avg_online_seconds),
                description: <FormattedMessage id="p.uo.kpi.avgOnlineDesc" />,
            },
        },
        {
            key: 'avgActive',
            statistic: {
                title: <FormattedMessage id="p.uo.kpi.avgActive" />,
                value: formatNumber(summary?.avg_active_count),
                description: <FormattedMessage id="p.uo.kpi.avgActiveDesc" />,
            },
        },
    ];

    return (
        <Permission permission="Users_Overview">
            <>
                <div className="mb-16">
                    <Typography.Text type="secondary">
                        <FormattedMessage id="p.uo.summary.dataAsOf" />：{dataAsOfText} · <FormattedMessage id="p.uo.summary.utcNote" />
                    </Typography.Text>
                </div>

                <Row className="mb-16" gutter={[16, 16]}>
                    {kpiCards.map(({ key, statistic }) => (
                        <Col key={key} lg={6} sm={12} xs={24}>
                            <StatisticCard loading={summaryLoading} statistic={statistic} />
                        </Col>
                    ))}
                </Row>

                <ProCard
                    title={formatMessage({ id: 'p.uo.trend.title' })}
                    extra={
                        <Space>
                            <Segmented
                                options={metricOptions}
                                value={metric}
                                onChange={(value) => setMetric(value as UserActiveMetric)}
                            />
                            <DatePicker.RangePicker
                                allowClear
                                value={range}
                                onChange={setRange}
                                placeholder={[
                                    formatMessage({ id: 'p.uo.trend.start' }),
                                    formatMessage({ id: 'p.uo.trend.end' }),
                                ]}
                            />
                        </Space>
                    }
                >
                    <ReactECharts option={chartOption} showLoading={trendLoading} style={{ height: 360 }} notMerge />
                </ProCard>
            </>
        </Permission>
    );
};

export default PageUsersOverview;
